/**
 * FunnelManagePage — criar e editar o funil de negociações.
 */
import React from 'react';
import { useAppContext } from '../../../contexts/AppContext';
import { useLeads } from '../../../contexts/leads/LeadsContext';
import { PageHeader } from '../../../components/layout/Header';
import { useFunnelForm } from './hooks/useFunnelForm';
import { useFunnelPage } from './hooks/useFunnelPage';
import FunnelToolbar from './components/FunnelToolbar';
import FunnelStagesList from './components/FunnelStagesList';

const FunnelManagePage: React.FC = () => {
    const { currentPage } = useAppContext();
    const {
        funnelStages,
        funnels,
        selectedFunnelId,
    } = useLeads();

    const isEditMode = Boolean(selectedFunnelId) && currentPage !== 'create-funnel';
    const currentFunnel = isEditMode
        ? funnels.find((funnel) => funnel.id === selectedFunnelId) ?? null
        : null;
    const filteredStages = isEditMode && selectedFunnelId
        ? funnelStages.filter((stage) => stage.funnelId === selectedFunnelId)
        : [];

    const form = useFunnelForm({
        funnel: currentFunnel,
        stages: filteredStages,
    });

    const {
        isSubmitting,
        handleBack,
        handleSave,
        handleTitleBlur,
        handleDuplicateFunnel,
        handleDeleteFunnel,
        handleRemoveStage,
    } = useFunnelPage({
        form,
        selectedFunnelId,
        isEditMode,
    });

    return (
        <main className="flex min-h-0 flex-1 flex-col bg-background">
            <PageHeader
                title={isEditMode ? 'Funil' : 'Novo funil'}
                subtitle="Monte o caminho que o lead percorre até o fechamento"
                titleClassName="text-xl font-semibold tracking-tight text-default"
                subtitleClassName="text-[13px] text-muted"
                className="mb-[18px] shrink-0"
                hideBackButton
            />

            <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-s3">
                <FunnelToolbar
                    funnelName={form.name}
                    onNameChange={form.setName}
                    onTitleBlur={handleTitleBlur}
                    onCancel={handleBack}
                    onSave={handleSave}
                    isSubmitting={isSubmitting}
                    canSave={form.isValid()}
                    isEditMode={isEditMode}
                    onDuplicate={handleDuplicateFunnel}
                    onDelete={handleDeleteFunnel}
                />

                <FunnelStagesList
                    stages={form.stageList}
                    draggedIndex={form.draggedIndex}
                    onNameChange={form.updateStageName}
                    onDeadlineChange={form.updateStageDeadline}
                    onConversionEventChange={form.updateStageConversionEvent}
                    onRemove={handleRemoveStage}
                    onDragStart={form.handleDragStart}
                    onDragOver={form.handleDragOver}
                    onDragEnd={form.handleDragEnd}
                    onAddStage={form.addStage}
                    onAddStageAt={form.addStageAt}
                />
            </div>
        </main>
    );
};

export default FunnelManagePage;
